// SPDX-License-Identifier: Elastic-2.0
/**
 * ControllersStatusSection — Settings card showing Matter + Zigbee
 * onboarding readiness (F-351 / T-011, functional spec US-1).
 *
 * Settings is a stack of self-contained `Card` sections (see
 * `PairedDevicesSection`); this is one more. It fetches the controller
 * readiness snapshot from `getControllers()` (T-008) via TanStack Query
 * and renders one row per protocol:
 *
 *   <icon>  Matter / Zigbee
 *           Ready · Not set up        ← status from `controller.available`
 *           <controller.detail>       ← server-supplied explainer
 *
 * When `ha_reachable === false` the controller list is meaningless, so we
 * render a single "Home Assistant unreachable" notice instead of the rows.
 *
 * i18n: all UI strings live under `settings.protocols.*` (EN canonical;
 * the remaining 5 locales are filled by T-012). The per-controller
 * `detail` text is server data, rendered verbatim.
 *
 * Only the component is exported (named + default) — no helper exports,
 * to satisfy `react-refresh/only-export-components` under max-warnings 0.
 */
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";

import { Banner } from "@/components/Banner/Banner";
import { Card } from "@/components/Card/Card";
import { Icon, type IconName } from "@/components/Icon/Icon";
import {
  getControllers,
  type Controller,
  type ControllersStatus,
} from "@/api/onboardingClient";

import styles from "./ControllersStatusSection.module.css";

/** Query key for the controller readiness snapshot. */
const CONTROLLERS_QUERY_KEY = ["onboarding", "controllers"] as const;

/** Display order + icon per protocol. */
const PROTOCOLS: { kind: Controller["kind"]; icon: IconName }[] = [
  { kind: "matter", icon: "plug" },
  { kind: "zigbee", icon: "wifi" },
];

export function ControllersStatusSection(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<ControllersStatus>({
    queryKey: CONTROLLERS_QUERY_KEY,
    queryFn: getControllers,
  });

  const byKind = new Map<Controller["kind"], Controller>(
    (data?.controllers ?? []).map((c) => [c.kind, c]),
  );

  return (
    <Card
      as="section"
      variant="elevated"
      padding="md"
      data-testid="settings-protocols"
    >
      <h2 className={styles.title}>{t("settings.protocols.section_title")}</h2>

      {isLoading ? (
        <p className={styles.loading} data-testid="settings-protocols-loading">
          {t("settings.protocols.loading")}
        </p>
      ) : data !== undefined && data.ha_reachable === false ? (
        <Banner
          kind="warning"
          message={t("settings.protocols.ha_unreachable")}
        />
      ) : (
        <ul className={styles.list}>
          {PROTOCOLS.map(({ kind, icon }) => {
            const controller = byKind.get(kind);
            const ready = controller?.available ?? false;
            return (
              <li
                key={kind}
                className={styles.row}
                data-testid={`settings-protocol-${kind}`}
              >
                <span className={styles.icon} aria-hidden="true">
                  <Icon name={icon} size={20} />
                </span>
                <div className={styles.rowBody}>
                  <div className={styles.rowMain}>
                    <span className={styles.name}>
                      {t(`settings.protocols.kind.${kind}`)}
                    </span>
                    <span
                      className={ready ? styles.statusReady : styles.statusOff}
                      data-testid={`settings-protocol-${kind}-status`}
                    >
                      {ready
                        ? t("settings.protocols.status_ready")
                        : t("settings.protocols.status_not_set_up")}
                    </span>
                  </div>
                  {controller?.detail ? (
                    <p
                      className={styles.detail}
                      data-testid={`settings-protocol-${kind}-detail`}
                    >
                      {controller.detail}
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default ControllersStatusSection;
